export type ArticleGenerationMode = "outline" | "article";

export type ArticleGenerationEvent = {
  type: "snapshot" | "complete" | "stopped" | "error";
  articleId: string;
  mode: ArticleGenerationMode;
  content: string;
  generating: boolean;
  message?: string;
  emittedAt: number;
};

export type GenerationConnectionStatus =
  | "idle"
  | "connecting"
  | "thinking"
  | "streaming"
  | "reconnecting"
  | "error";

type GenerationStreamCallbacks = {
  onEvent: (event: ArticleGenerationEvent) => void;
  onStatusChange: (
    status: GenerationConnectionStatus,
    message?: string
  ) => void;
};

type EventSourceFactory = (
  url: string,
  eventSourceInitDict?: EventSourceInit
) => EventSource;

export class ArticleGenerationStream {
  private eventSource: EventSource | null = null;
  private hasReceivedContent = false;

  constructor(
    private readonly url: string,
    private readonly callbacks: GenerationStreamCallbacks,
    private readonly createEventSource: EventSourceFactory = (url, init) =>
      new EventSource(url, init)
  ) {}

  connect(): void {
    this.close();
    this.hasReceivedContent = false;
    this.callbacks.onStatusChange("connecting");

    try {
      const eventSource = this.createEventSource(this.url, {
        withCredentials: true,
      });
      this.eventSource = eventSource;

      eventSource.onopen = () => {
        if (this.eventSource === eventSource) {
          this.callbacks.onStatusChange(
            this.hasReceivedContent ? "streaming" : "thinking"
          );
        }
      };
      eventSource.onerror = () => {
        if (this.eventSource === eventSource) {
          this.callbacks.onStatusChange(
            "reconnecting",
            "Connection interrupted—reconnecting…"
          );
        }
      };
      eventSource.addEventListener("generation", (message) => {
        if (this.eventSource !== eventSource) {
          return;
        }
        try {
          const event = JSON.parse(
            (message as MessageEvent<string>).data
          ) as ArticleGenerationEvent;
          if (!this.hasReceivedContent && event.content) {
            this.hasReceivedContent = true;
            this.callbacks.onStatusChange("streaming");
          }
          this.callbacks.onEvent(event);
        } catch {
          this.callbacks.onStatusChange(
            "error",
            "Received an invalid generation update"
          );
        }
      });
    } catch {
      this.callbacks.onStatusChange(
        "error",
        "Live generation is unavailable in this browser"
      );
    }
  }

  close(): void {
    this.eventSource?.close();
    this.eventSource = null;
  }
}
